import {redirect} from '@sveltejs/kit';
// In .js files you can't use $ to reference a store (e.g. $request); you need
// to use the 'get()' (and '.set()') function instead
import {get} from 'svelte/store';

import {app_url} from '$lib/svelte_1848_settings.js';
import {post_call, get_requested_state, process_dossier, check_logged_in} from '$lib/utils.js';
import {request, previous_query, previous_date_range, total, docs, categories_all, facet_categories, categories, dossiers, size, has_more, meetings_total, clicked_category, last_refresh, total_new_docs, do_load} from '$lib/store.js';

// Function which recursively loops over the nested facets and sets all
// doc_counts to zero
const doc_counts2zero = (obj) => {
    Object.keys(obj).forEach(key => {
        if (key === 'doc_count') {
            obj[key] = 0;
        }

        if (typeof obj[key] === 'object' && obj[key] !== null) {
            doc_counts2zero(obj[key])
        }
    })
};

export async function load({url, fetch, data, depends}) {
    // This allows us to manually rerun this load function by running
    // invalidate('app:page')
    depends('app:page');

    let logged_in = check_logged_in(data);

    // If the user is not logged in and requests a dossier, then redirect to the
    // login page
    if (!logged_in && url.searchParams.get('dossier')) {
        throw redirect(302, `${app_url}login`);
    }

    // Get the requested state (some of these values are also available as
    // stores, but we return all values here as normal variables for
    // readability)
    let [query, dossier_id, date_range, date_range_agenda, filters] = get_requested_state(url, data);

    // By default we want to run the requests (do_load == true), but not always
    // (e.g., when navigating back from a document to the feed)
    if (get(do_load)) {
        total_new_docs.set(0);

        // We store the current search request as store variable as we need to
        // reuse it to do pagination requests during infinite scroll
        request.set({
            endpoint: '/search/',
            body: {
                // The commented parameters are shown as documentation
                query: query,
                //sort: "publication_date",
                filters: filters,
                //order: "desc",
                size: get(size),
                from: 0,
                dateRange: date_range,
                //highlight: false,
                //dossierID: null,
                //search_type: count
            }
        });

        // We only need the agenda items count for $meetings_total
        let request_agenda_count = {
            endpoint: '/search/agenda/',
            body: {
                query: query,
                dateRange: date_range_agenda,
                search_type: 'count'
            }
        };

        // If the user is logged in and a dossier is selected, update the
        // requests with the correct endpoints and body; remove the query as
        // the database already knows it:
        // If the 'all' dossier is selected
        if (logged_in) {
            if (dossier_id === 'all') {
                request.update(n => {
                    n.body.query = null;
                    return {endpoint: '/search/dossiers/', body: n.body}
                });

                request_agenda_count.endpoint = '/search/agenda/dossiers/';
                request_agenda_count.body.query = null;
            // Otherwise a normal dossier is selected
            } else if (dossier_id) {
                request.update(n => {
                    n.body.query = null;
                    n.body.dossierID = dossier_id;
                    return {endpoint: '/search/dossier/', body: n.body}
                });

                request_agenda_count.endpoint = '/search/agenda/dossier/';
                request_agenda_count.body.query = null;
                request_agenda_count.body.dossierID = dossier_id;
            }
        }

        // Get the search request response and save its data in stores
        let response = await post_call(get(request), fetch, data);
        total.set(response?.result?.total);
        docs.set(response?.result?.hits);

        // Set has_more to false if we collected the last batch/page of
        // documents; this will stop the infinite scroll
        if (get(size) >= get(total)) {
            has_more.set(false);
        } else {
            has_more.set(true);
        }

        // Get the agenda count request response and save its data in stores;
        // Only run this if:
        //  - $meetings_total is null (i.e. when the feed is first loaded)
        //  - or if the query or date filter has changed
        // It thus doesn't run when navigating from the agenda feed to this
        // feed or when a category is clicked, because the count will be the
        // same in both cases
        if (get(meetings_total) === null || get(previous_query) !== query || JSON.stringify(get(previous_date_range)) !== JSON.stringify(date_range)) {
            let response_agenda_count = await post_call(request_agenda_count, fetch, data);
            meetings_total.set(response_agenda_count?.result?.total);
        }

        // Check if the current dossier has categories
        let dossier_has_categories = false;
        if (logged_in && dossier_id) {
            get(dossiers)?.forEach(dossier => {
                if (dossier.id == dossier_id) {
                    if (dossier.notification_item_category_filter.length) {
                        dossier_has_categories = true;
                    }
                }
            });
        }

        // In some cases we need to do an extra search call without filters to
        // get the doc_counts of all categories (because a search call with
        // specific categories only returns the doc_counts of those
        // categories, but we want to show the doc_counts of all categories):
        // - If the 'all' dossier and filters/categories are selected, use the
        //   'search/dossiers/' endpoint
        // - Else if there are selected filters/categories (except when a
        //   category was clicked, because the doc_counts won't change then) or
        //   if a dossier is selected that has categories, use the 'search/'
        //   endpoint
        let response_facets;
        if (logged_in) {
            if (Object.keys(filters).length && dossier_id === 'all') {
                response_facets = await post_call({
                    endpoint: '/search/dossiers/',
                    body: {dateRange: date_range, search_type: 'count'}
                }, fetch, data);
            } else if ((Object.keys(filters).length && !get(clicked_category)) || dossier_has_categories) {
                response_facets = await post_call({
                    endpoint: '/search/',
                    body: {query: query, dateRange: date_range, search_type: 'count'}
                }, fetch, data);
            }
        }

        // If we have response_facets, then use those facets
        let facets;
        if (response_facets) {
            facets = response_facets.result?.facets;
        // Otherwise use the normal response facets
        } else {
            facets = response?.result?.facets;
        }

        // Updates the category doc_counts in facet_categories; don't run if
        // the user clicked a category, because the doc_counts then don't
        // change
        if (!get(clicked_category)) {
            // Set all category doc_counts to zero (we need to do this manually
            // because the API response doesn't include a category if the
            // doc_count is zero)
            doc_counts2zero(get(facet_categories));

            // Take the (new) category doc_counts from facets and set them in
            // $facet_categories
            facets?.categories?.forEach(category => {
                var arr = category.key.split('|');
                var tmp = get(facet_categories);

                for (var i=0,n=arr.length; i<n; i++) {
                    if (i == arr.length - 1) {
                        tmp[arr[i]]['doc_count'] = category.doc_count;
                    }
                    tmp = tmp[arr[i]];
                }
            });

            // Tell the Svelte compiler that this object has changed and thus
            // needs to update the HTML
            facet_categories.set(get(facet_categories));
        }

        // If a dossier is selected this will get and set the categories
        // associated with that dossier
        process_dossier();

        // Save the date range and query for later comparison
        previous_date_range.set({...date_range});
        previous_query.set(query);
    } else {
        // If do_load was 'false', set it to the default value of 'true' again
        do_load.set(true);
    }

    // Set last_refresh to the current timestamp after a page load/change. We
    // use it to set an interval to check if there are new documents every 30
    // seconds
    last_refresh.set(new Date().toLocaleString('sv').replace(' ', 'T') + '+01:00');
}
